import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { UseInfiniteQueryOptions } from '@tanstack/react-query/src/types';
import { useMemo } from 'react';
import { PreparedSortCriteria, SortCriteria } from '@steelbuy/api-integration';
import { entries } from '@steelbuy/util';
import { createFilterString } from './createFilterString';
import { createSortString } from './createSortString';
import { FilterComparator, FilterCriteriaEntry, ResponseModel } from './types';
import axios from '../axios';

type HttpMethod = 'GET' | 'POST';

interface QueryOptions<Model> {
    pageSize?: number;
    offset?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filter?: Record<string, any>;
    sort?: SortCriteria<Model> | PreparedSortCriteria | string;
    status?: string;
    params?: Record<string, string>;
}

interface UsePaginatedOptions<Model, TError = unknown> {
    queryKey: QueryKey;
    url: string;
    method?: HttpMethod;
    requestOptions: QueryOptions<Model>;
    queryOptions?: Omit<
        UseInfiniteQueryOptions<ResponseModel<Model[]>, TError>,
        'queryKey' | 'queryFn' | 'getNextPageParam'
    >;
    mapFn?: (data: Model) => Model;
    itemsKey?: string;
    keepPreviousData?: boolean;
}
type PaginatedDataOptions = {
    url: string;
    pageParam: number;
};

type GenericPaginatedDataOptions<ViewModel, ApiModel> = {
    url: string;
    pageParam: number;
    mapFn: (data: ApiModel) => ViewModel;
};

const DEFAULT_PAGE_SIZE = 10;

const composeFilterCriteria = <Model>(data: Record<keyof Model, string | number>) =>
    entries(data)
        .filter(([_, value]) => typeof value !== 'undefined')
        .map(
            ([property, value]): FilterCriteriaEntry<Model> => ({
                property,
                comparator: FilterComparator.EQUAL,
                value,
            })
        );

const toPostSort = <Model>(sort?: SortCriteria<Model> | PreparedSortCriteria | string) => {
    if (!sort || typeof sort === 'string') return undefined;

    if (Array.isArray(sort)) {
        const first = sort[0];
        if (!first) return undefined;

        return [
            {
                key: first.property,
                direction: first.direction?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
            },
        ];
    }

    return undefined;
};

const getPaginatedData = async <ViewModel, ApiModel = ViewModel>(
    options: QueryOptions<ViewModel> &
        (PaginatedDataOptions | GenericPaginatedDataOptions<ViewModel, ApiModel>) & {
            method?: HttpMethod;
        }
): Promise<ResponseModel<ViewModel[]>> => {
    const { pageSize = DEFAULT_PAGE_SIZE, filter, sort, pageParam = 0, url, status, params, method = 'GET' } = options;
    let data: ResponseModel<ApiModel[]>;

    if (method === 'POST') {
        const body = {
            filters: filter,
            sort: toPostSort(sort),
            pagination: {
                offset: pageParam * pageSize,
                limit: pageSize,
            },
        };

        const response = await axios.post<ResponseModel<ApiModel[]>>(url, body, {
            params: {
                ...(status && { status }),
                ...params,
            },
        });

        data = response.data;
    } else {
        const response = await axios.get<ResponseModel<ApiModel[]>>(url, {
            params: {
                limit: pageSize,
                offset: pageParam * pageSize,
                ...(filter && {
                    filter: createFilterString(composeFilterCriteria(filter)),
                }),
                ...(sort && {
                    sort: typeof sort === 'string' ? sort : createSortString(sort),
                }),
                ...(status && { status }),
                ...params,
            },
        });

        data = response.data;
    }

    if ('mapFn' in options && typeof options.mapFn === 'function') {
        const { items } = data;
        return {
            ...data,
            items: items.map(options.mapFn),
        };
    }
    return data as unknown as ResponseModel<ViewModel[]>;
};

export const usePaginatedQuery = <Model, TError = unknown>({
    queryKey,
    queryOptions,
    requestOptions,
    url,
    mapFn,
    keepPreviousData,
    method = 'GET',
}: UsePaginatedOptions<Model, TError>) => {
    const { data: queryData, ...rest } = useInfiniteQuery<ResponseModel<Model[]>, TError>({
        ...queryOptions,
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: ({ pageParam }) =>
            getPaginatedData<Model>({
                ...requestOptions,
                pageParam,
                url,
                mapFn,
                method,
            }),
        getNextPageParam: ({ limit, offset, total }) =>
            offset + (requestOptions?.pageSize || DEFAULT_PAGE_SIZE) < total ? offset / limit + 1 : undefined,
        keepPreviousData,
    });
    const { data, total } = useMemo(
        () => ({
            data: queryData?.pages.flatMap((e) => e.items),
            total: queryData?.pages?.[0]?.total,
        }),
        [queryData]
    );

    return { ...rest, data, total };
};
